<template>
  <div>
    <breadcrumbs :home="vessel.name + ': Adjust inventory'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row dense>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh(false)"/>
            </v-col>
            <v-col class="text-caption ml-5 mt-3 grey--text">{{vesselContents}}</v-col>
          </v-row>

          <v-row dense v-if="this.vessel.contents.volume">
            <v-col>
              <v-radio-group v-model="transaction.specifyDifference" dense class="mt-0" row
                            @change="$nextTick(() => $refs.form.validate())">
                <v-radio label="Adjust by difference" :value="true"/>
                <v-radio label="Adjust to specified volume/value" :value="false"/>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row dense v-show="transaction.specifyDifference">
            <v-col cols="4" sm="2" lg="1">
              <v-btn-toggle mandatory dense v-model="negative" @change="calculateValue = negative">
                <v-btn icon :disabled="!edit" :value="false"><v-icon small>mdi-plus</v-icon></v-btn>
                <v-btn icon :disabled="!edit" :value="true"><v-icon small>mdi-minus</v-icon></v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <alcohol-input label="Adjust by"
                              :spirit="spirit || !this.vessel.contents.volume" :litres.sync="litres" :kg.sync="kg" :strength.sync="strength"
                              :tare="vessel.tare" v-model="volume" :disabled="!edit" :hint="vesselVolume"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <decimal-number-input label="Value" v-model="value" :disabled="calculateValue"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <v-checkbox label="Calculate value" v-model="calculateValue" dense class="mt-1" :disabled="!negative"/>
            </v-col>
          </v-row>

          <div v-show="!transaction.specifyDifference">
            <v-row dense>
              <v-col cols="6" sm="3" lg="2">
                <alcohol-input label="Adjusted volume"
                                :spirit="spirit || (!this.vessel.contents.volume && contents === 'Spirit')"
                                :litres.sync="transaction.litres" :kg.sync="transaction.kg" :strength.sync="strength"
                                :tare="vessel.tare" v-model="transaction.volume" :disabled="!edit" :hint="vesselVolume"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Adjusted value" v-model="transaction.value" :hint="'$'+$number(vessel.contents.value,2)"/>
              </v-col>
            </v-row>
            <v-row dense v-if="!vessel.contents.volume">
              <v-col cols="4" sm="3" lg="2">
                <v-select :items="['Spirit','Wine','Non-excise']" v-model="contents" label="Type" outlined dense @change="toggleType()"/>
              </v-col>
              <v-col cols="12" sm="6" lg="4">
                <v-autocomplete v-model="transaction.alcoholId" outlined dense label="Contents" :rules="[fieldRules.required]"
                                :items="alcoholItems" item-text="name" item-value="id" no-data-text="None available"/>
              </v-col>

              <v-col cols="6" sm="3" lg="2">
                <v-text-field outlined dense label="Lot" v-model="transaction.lot" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col class="text-caption mb-6">Adjustment will be saved as "Adjust by difference" with calculated difference</v-col>
            </v-row>
          </div>

          <v-row v-if="values && company.connectedToAccounting">
            <v-col cols="12" sm="6" lg="4">
              <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                              :accounts="accounts"
                              hint="An account to balance the inventory asset account. Usually an inventory shrinkage expense or cost of goods account."/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense counter="255" :rules="[fieldRules.required,fieldRules.maxLength(255)]"/>
            </v-col>
          </v-row>
          <v-row v-if="(!this.vessel.contents.volume && contents !== 'Non-excise') || (this.vessel.contents.volume && vessel.contents.alcoholType !== 'NonExcise')">
            <v-col cols="12" sm="10" class="text-caption">
              <div>
                <strong>Adjustments appear on line D of the
                {{(this.vessel.contents.volume && vessel.contents.alcoholType === 'Spirit') ||  (!this.vessel.contents.volume && contents === 'Spirit') ? 'B266' : 'B263'}}
                </strong>
              </div>
              <div><em>Adjustments may be necessary to balance the physical inventory with the book inventory.
              The closing or book inventory must agree with the physical inventory for the fiscal month.
              All adjusting entries are subject to audit.</em></div>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('production.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'ExciseVesselAdjustInventory',
  mixins: [confirmLeave],
	props: {
    vesselId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      vessel: {name:'',contents:{}},
      valid:true,
      saving:false,
      accounts: [],
      spirits: [],
      wines:[],
      nonExcises: [],
      spiritLot: '',
      wineLot: '',
      nonExciseLot: '',
      contents: 'Spirit',
      calculateValue: false,
      value: 0,
      volume: 0,
      litres: 0,
      kg: 0,
      strength: 0,
      negative: false,
      transaction: {vesselId:this.vesselId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    specifyDifference: true, alcoholId: '', lot: '',
                    volume: 0, litres:0, kg:0, strength: 0, value:0, comments: '', expenseAccountRef:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    },
    spirit() {
      return this.vessel.contents?.alcoholType === 'Spirit';
    },
    vesselContents() {
      return this.vessel.contents.volume ? this.vessel.contents.alcoholName : '';
    },
    vesselVolume() {
      if (!this.transactionId)
        return this.vessel.contents.volume ? `${this.$number(this.vessel.contents.volume, 3)} ${this.spirit?'LAA':'litres'}` : '(empty)';
    },
    alcoholItems() {
      return this.contents === 'Spirit' ? this.spirits : (this.contents === 'Wine' ? this.wines : this.nonExcises);
    }
  },
  watch: {
    volume() {
      this.dirty = true;
    },
    calculateValue() {
      this.dirty = true;
    },
    value() {
      this.dirty = true;
    },
    litres() {
      this.dirty = true;
    },
    kg() {
      this.dirty = true;
    },
    negative() {
      this.dirty = true;
    },
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);

    const results = await this.$apiCall(`query ExciseVesselAdjustInventory($currency:String!,$id:ID!,$edit:Boolean!) {
                                              accounts(currency:$currency) {
                                                ref,parentRef,name,type,typeName
                                              }
                                              spiritLot: lotUnique(prefix:"S")
                                              wineLot: lotUnique(prefix:"W")
                                              nonExciseLot: lotUnique(prefix:"N")
                                              exciseVesselAdjustInventory(id:$id) @include(if:$edit) {
                                                id,vesselId,date,volume,litres,kg,strength,value,comments,expenseAccountRef,
                                                specifyDifference,alcoholId,lot
                                              }
                                              companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                            }`,
                                        {currency:this.company.settings.homeCurrency,
                                         id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.accounts = results.data.accounts;
    this.spiritLot = results.data.spiritLot;
    this.wineLot = results.data.wineLot;
    this.nonExciseLot = results.data.nonExciseLot;
    if (this.transactionId) {
      this.transaction = results.data.exciseVesselAdjustInventory;
      this.calculateValue = !this.transaction.value && this.transaction.volume < 0;
      this.negative = this.transaction.volume < 0;
      this.value = Math.abs(this.transaction.value || 0);
      this.volume = Math.abs(this.transaction.volume);
      this.litres = Math.abs(this.transaction.litres);
      this.kg = Math.abs(this.transaction.kg);
      this.strength = this.transaction.strength;
    }
    if (!this.transactionId) {
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
      this.edit = true;
    }
    await this.refresh(true);
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    async refresh() {
      const results = await this.$apiCall(`query ExciseVesselAdjustInventory($id:ID!, $date:DateTime) {
                                                exciseVessel(id:$id) {
                                                  id,name,tare,
                                                  contents(date:$date) {alcoholType,alcoholName,alcoholId,volume,litres,kg,strength,value,lot}
                                                }
                                              }`,
                                          {id:this.transaction.vesselId || this.vesselId, date:this.transaction.date});
      if (!results.error) {
        this.vessel = results.data.exciseVessel;
        this.transaction.volume = this.vessel.contents.volume;
        this.transaction.litres = this.vessel.contents.litres;
        this.transaction.kg = this.vessel.contents.kg;
        this.transaction.value = this.vessel.contents.value;
        this.transaction.alcoholId = this.vessel.contents.alcoholId;
        this.transaction.specifyDifference = !!this.vessel.contents.volume;
        this.transaction.lot = this.transaction.specifyDifference ? this.vessel.contents.lot : this.toggleType();
        this.strength = this.vessel.contents.strength;
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    toggleType() {
      this.transaction.alcoholId=''
      this.transaction.lot = this.contents === 'Spirit' ? this.spiritLot : (this.contents === 'Wine' ? this.wineLot : this.nonExciseLot);
      return this.transaction.lot;
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        const transaction = {...this.transaction, strength:this.strength};
        if (transaction.specifyDifference)
          Object.assign(transaction, {value:this.calculateValue ? null : (this.negative?-this.value:this.value),
                                      volume:this.negative?-this.volume:this.volume,
                                      litres:this.negative?-this.litres:this.litres,
                                      kg:this.negative?-this.kg:this.kg,
                                      });
        this.saving = true;
        const results = await this.$apiCall(`mutation ExciseVesselAdjustInventory($input: ExciseVesselAdjustInventoryInput!) {
                                                exciseVesselAdjustInventoryCreate(input:$input) {id}
                                              }`,
                                            {input:transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} adjust inventory transaction for ${this.vessel.name}`});
        }
      }
      else
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
		}
	}
}
</script>